/**
 * lib/reviews/card-rating.ts — the ONE rule for a retailer-review rating on a
 * product card (FRIDGE-786, owner decision 09/07/2026: real retailer-product
 * evidence, a transparent source, enough review volume; never staff testing,
 * never an average of averages without counts, absent where unsupported).
 *
 * The figure is `fin_review_rollup.weighted_rating` — the same per-FIN
 * cross-source aggregate the product page's Customer Reviews header and the
 * FRIDGE-498 "highest-rated" rail already render — so a product carries one
 * number sitewide. The floor is shared with that rail (recommendations-server)
 * for the same reason: one glowing review must never outrank a thousand.
 *
 * PURE and client-safe: no server imports, so the card component can format
 * the label and the server loader can apply the floor from one definition.
 * The list-page schema rule is untouched — nothing here emits a schema rating node.
 */
import { reviewSourceLabel } from '@/lib/render-policy/display-reviews'

/** Reviews behind a rating before a card may show it (FRIDGE-786 §2 proposal, adopted). */
export const MIN_REVIEWS_FOR_RATING = 20

/** The rollup's generic bucket is data plumbing, not a shopper-meaningful source name. */
const PLUMBING_SOURCES = new Set(['aggregated_reviews'])

export interface CardRating {
  /** Cross-source weighted average, 0.1 steps, 1..5. */
  rating: number
  /** Retailer reviews behind it — always >= MIN_REVIEWS_FOR_RATING. */
  reviews: number
  /** Shopper-facing source names, largest review count first, de-duplicated. */
  sources: string[]
}

/** The rollup row as PostgREST delivers it (numerics may arrive as strings). */
export interface RollupRowLike {
  weighted_rating: number | string | null
  total_reviews: number | string | null
  sources?: unknown
}

function toNum(v: unknown): number | null {
  if (v === null || v === undefined) return null
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

function sourceNames(sources: unknown): string[] {
  if (!Array.isArray(sources)) return []
  const rows = sources
    .map((s) => {
      if (!s || typeof s !== 'object') return null
      const source = (s as { source?: unknown }).source
      const count = toNum((s as { count?: unknown }).count) ?? 0
      return typeof source === 'string' && count > 0 && !PLUMBING_SOURCES.has(source) ? { source, count } : null
    })
    .filter((s): s is { source: string; count: number } => s !== null)
    .sort((a, b) => b.count - a.count)
  const out: string[] = []
  for (const r of rows) {
    const label = reviewSourceLabel(r.source)
    if (!out.includes(label)) out.push(label)
  }
  return out
}

/**
 * A card rating, or null when the evidence does not support one: no row, a
 * non-numeric or non-positive average, or fewer than the floor's reviews.
 */
export function cardRatingFromRollup(row: RollupRowLike | null | undefined): CardRating | null {
  if (!row) return null
  const reviews = toNum(row.total_reviews)
  const raw = toNum(row.weighted_rating)
  if (reviews === null || raw === null) return null
  if (reviews < MIN_REVIEWS_FOR_RATING || raw <= 0) return null
  const rating = Math.min(5, Math.round(raw * 10) / 10)
  return { rating, reviews: Math.floor(reviews), sources: sourceNames(row.sources) }
}

/** "1204" → "1,204" without depending on the runtime locale. */
export function formatReviewCount(n: number): string {
  return Math.floor(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** What assistive tech hears for the stars: rating, count and provenance in one sentence. */
export function ratingAriaLabel(r: CardRating): string {
  return `${r.rating.toFixed(1)} out of 5 stars from ${formatReviewCount(r.reviews)} retailer reviews`
}

/** The source line — the tooltip on the card: "2,182 retailer reviews · Best Buy, Walmart". */
export function ratingSourceLine(r: CardRating): string {
  const base = `${formatReviewCount(r.reviews)} retailer reviews`
  return r.sources.length > 0 ? `${base} · ${r.sources.join(', ')}` : base
}
